import { useMemo } from 'react';

import PERM from 'constants/core/perm.const';
import { useAccessIds, useCustomPermission } from 'providers/Access.provider';

/**
 * Backward-compatible appointment permission flags.
 *
 * For radio-button permissions (create / move / delete) the backend assigns
 * exactly ONE of {all_staff, personal, no_access}.  When no appointment
 * permission is present at all (legacy users, or the feature flag hasn't been
 * turned on yet) every check defaults to "unrestricted" so existing behaviour
 * is preserved.
 *
 * For checkbox permissions (price / duration) the same backward-compat rule
 * applies: editing is allowed unless the permission system is active AND the
 * specific permission was not granted.
 */
const useAppointmentPermissions = () => {
  const canCreateAll = useCustomPermission(PERM.canCreateAppointmentAllStaff);
  const canCreatePersonal = useCustomPermission(PERM.canCreateAppointmentPersonal);
  const canCreateNoAccess = useCustomPermission(PERM.canCreateAppointmentNoAccess);

  const canMoveAll = useCustomPermission(PERM.canMoveAppointmentAllStaff);
  const canMovePersonal = useCustomPermission(PERM.canMoveAppointmentPersonal);
  const canMoveNoAccess = useCustomPermission(PERM.canMoveAppointmentNoAccess);

  const canDeleteAll = useCustomPermission(PERM.canDeleteAppointmentAllStaff);
  const canDeletePersonal = useCustomPermission(PERM.canDeleteAppointmentPersonal);
  const canDeleteNoAccess = useCustomPermission(PERM.canDeleteAppointmentNoAccess);

  const rawCanEditPrice = useCustomPermission(PERM.canEditAppointmentPrice);
  const rawCanEditDuration = useCustomPermission(PERM.canEditAppointmentDuration);

  const { accessStylistId } = useAccessIds();

  return useMemo(() => {
    const hasAnyCreatePerm = canCreateAll || canCreatePersonal || canCreateNoAccess;
    const hasAnyMovePerm = canMoveAll || canMovePersonal || canMoveNoAccess;
    const hasAnyDeletePerm = canDeleteAll || canDeletePersonal || canDeleteNoAccess;

    const permsActive = hasAnyCreatePerm || hasAnyMovePerm || hasAnyDeletePerm || rawCanEditPrice || rawCanEditDuration;

    return {
      accessStylistId,

      isCreateBlocked: hasAnyCreatePerm && canCreateNoAccess && !canCreateAll && !canCreatePersonal,
      isCreatePersonalOnly: hasAnyCreatePerm && canCreatePersonal && !canCreateAll,

      isMoveBlocked: hasAnyMovePerm && canMoveNoAccess && !canMoveAll && !canMovePersonal,
      isMovePersonalOnly: hasAnyMovePerm && canMovePersonal && !canMoveAll,

      isDeleteBlocked: hasAnyDeletePerm && canDeleteNoAccess && !canDeleteAll && !canDeletePersonal,
      isDeletePersonalOnly: hasAnyDeletePerm && canDeletePersonal && !canDeleteAll,

      canEditPrice: !permsActive || rawCanEditPrice,
      canEditDuration: !permsActive || rawCanEditDuration,
    };
  }, [
    accessStylistId,
    canCreateAll,
    canCreateNoAccess,
    canCreatePersonal,
    canDeleteAll,
    canDeleteNoAccess,
    canDeletePersonal,
    canMoveAll,
    canMoveNoAccess,
    canMovePersonal,
    rawCanEditDuration,
    rawCanEditPrice,
  ]);
};

export default useAppointmentPermissions;
